/* import __COLOCATED_TEMPLATE__ from './schedule-creator.hbs'; */
import Component from '@glimmer/component';
import { action } from '@ember/object';
import { createCache, getValue } from '@glimmer/tracking/primitives/cache';
import clsx from 'clsx';

const CLASS_NAME = 'SH-c-schedule-creator';

export const createScheduleItem = (type = '', description = '', date = '', custom = false) => ({
  guid: crypto.randomUUID(),
  type,
  description,
  date,
  custom,
});

class ScheduleCreatorComponent extends Component {
  get classNames() {
    return clsx(
      this.args.class,
      CLASS_NAME,
    );
  }

  get className() {
    return CLASS_NAME;
  }

  get minDate() {
    if (this.args.minDate) {
      return this.args.minDate;
    }

    const newDate = new Date();
    newDate.setHours(0, 0, 0, 0);
    return newDate;
  }

  #scheduleItems = createCache(() => {
    const schedule = [...this.args.schedule];

    // provide blank fields if there are zero or they began filling the previous field
    const lastScheduleItem = schedule[schedule.length - 1];
    if (schedule.length === 0 || lastScheduleItem?.description || lastScheduleItem?.date) {
      schedule.push(createScheduleItem());
    }

    return schedule;
  });

  get scheduleItems() {
    return getValue(this.#scheduleItems);
  }

  get lastScheduleItemsIndex() {
    return this.scheduleItems.length - 1;
  }

  #filteredOptions = createCache(() => {
    const { options } = this.args;
    const scheduleItemCounts = this.scheduleItems.reduce((accum, { type }) => {
      if (!type) return accum;

      const count = accum.get(type) || 0;
      accum.set(type, count + 1);
      return accum;
    }, new Map());

    return options.filter((option) => {
      if (!option.limit) return true;

      const count = scheduleItemCounts.get(option.model) ?? 0;
      return count < option.limit;
    });
  });

  get filteredOptions() {
    return getValue(this.#filteredOptions);
  }

  get optionsMap() {
    const { filteredOptions, scheduleItems } = this;
    const { options } = this.args;

    const modelToOptionMap = options.reduce((accum, option) => {
      accum.set(option.model, option);
      return accum;
    }, new Map());

    // filtered options are the remaining available dropdown options
    const availableModels = new Set(filteredOptions.map(({ model }) => model));

    return scheduleItems.reduce((accum, scheduleItem, index) => {
      const selectedOption = modelToOptionMap.get(scheduleItem.type);
      if (selectedOption) {
        // prepend selected option if filtered out of available options due to limit
        accum[index] = availableModels.has(selectedOption.model) ?
          filteredOptions
          : [selectedOption, ...filteredOptions];
      } else {
        accum[index] = filteredOptions;
      }

      return accum;
    }, {});
  }

  /**
   *
   */
  @action
  handleDeleteScheduleItem(index) {
    // remove index
    const schedule = [...this.args.schedule];
    schedule.splice(index, 1);

    // update with changes
    this.args.onupdate?.(schedule);
  }

  /**
   *
   */
  @action
  handleTypeDropdownChange(index, selection) {
    const schedule = [...this.args.schedule];

    if (selection) {
      const description = selection.custom ? '' : selection.key;

      schedule[index] = {
        ...schedule[index],
        type: selection.model,
        description,
        custom: selection.custom,
      };
    } else {
      // cleared selection
      schedule[index] = createScheduleItem('', '', schedule[index].date);
    }

    // update with changes
    this.args.onupdate?.(schedule);
  }

  /**
   *
   */
  @action
  handleTypeCustomInput(index, value) {
    const schedule = [...this.args.schedule];
    schedule[index] = { ...schedule[index], description: value };

    // update with changes
    this.args.onupdate?.(schedule);
  }

  /**
   *
   */
  @action
  handleDateInput(index, value) {
    const schedule = [...this.args.schedule];
    schedule[index] = { ...schedule[index], date: value };

    // update with changes
    this.args.onupdate?.(schedule);
  }

  /**
   * Handle Pikaday's accessability keybinds
   *
   * @param {number} index
   * @param {Event} e
   */
  @action
  handleDateKeyDown(index, e) {
    if (e.defaultPrevented) {
      return;
    }

    switch (e.key) {
      case 'ArrowUp':
      case 'ArrowRight':
      case 'ArrowDown':
      case 'ArrowLeft':
      case 'Space':
        // readonly input for datepicker would've scrolled page
        e.preventDefault();

        break;


      case 'Backspace':
      case 'Delete':
        e.preventDefault();

        // fire update event since pikaday does not
        const schedule = [...this.args.schedule];
        schedule[index] = { ...schedule[index], date: '' };

        // update with changes
        this.args.onupdate?.(schedule);

        break;
    }
  }
}

export default ScheduleCreatorComponent;
